import { z } from 'zod/v3';

// GitHub sync metadata stored in Doc.metadata
export const GitHubDocMetadataSchema = z.object({
  // GitHub file information
  githubSha: z.string().optional(), // SHA of the file in GitHub
  githubPath: z.string().optional(), // Path in GitHub repository
  githubUrl: z.string().optional(), // Direct URL to file on GitHub
  
  // Sync tracking
  lastGithubSync: z.date().optional(), // When last synced with GitHub
  lastSyncCommit: z.string().optional(), // Commit SHA from last sync
  syncConflict: z.boolean().optional(), // Whether there's a sync conflict
  
  // Change tracking
  locallyModified: z.boolean().optional(), // Modified locally since last sync
  githubModified: z.boolean().optional(), // Modified on GitHub since last sync
  
  // Sync configuration
  syncEnabled: z.boolean().optional(), // Whether sync is enabled for this doc
  syncDirection: z.enum(['pull', 'push', 'bidirectional']).optional(),
});

export type GitHubDocMetadata = z.infer<typeof GitHubDocMetadataSchema>;

// GitHub integration configuration stored in Integration.config
export const GitHubIntegrationConfigSchema = z.object({
  // Authentication
  accessToken: z.string(),
  
  // Repository configuration
  repository: z.string(), // format: "owner/repo"
  branch: z.string().default('main'),
  syncPath: z.string().optional(), // e.g., "/docs"
  // Trigger scoping
  directoryFilters: z.array(z.string()).optional(), // e.g., ["docs/**", "packages/*/CHANGELOG.md"]
  
  // Sync settings
  syncMode: z.enum(['mirror', 'collaborate']).default('collaborate'),
  autoSync: z.boolean().default(true),
  
  // Webhook configuration
  webhookId: z.string().optional(),
  webhookSecret: z.string().optional(),
  
  // Sync state
  lastSyncedCommit: z.string().optional(),
  syncStatus: z.object({
    lastSync: z.date().optional(),
    lastError: z.string().optional(),
    pendingChanges: z.number().default(0),
  }).optional(),
});

export type GitHubIntegrationConfig = z.infer<typeof GitHubIntegrationConfigSchema>;

// GitHub webhook payload types
export const GitHubPushEventSchema = z.object({
  ref: z.string(),
  after: z.string(),
  repository: z.object({
    full_name: z.string(),
  }),
  commits: z.array(z.object({
    added: z.array(z.string()),
    modified: z.array(z.string()),
    removed: z.array(z.string()),
  })),
});

export type GitHubPushEvent = z.infer<typeof GitHubPushEventSchema>;
